/**
 * components/PreworkBanner.js — Гол хуудаснуудад харагдах автомат banner.
 *
 * Хоёр горимтой:
 *   • mode="worker" — ӨЭА даалгавар авсан ажилтны banner ("/me" дотор)
 *   • mode="ashua"  — АШУА (мастер/хэсгийн дарга)-ийн banner ("/highrisk/*" дээр)
 *   • mode-гүй      — хоёулыг хамтад нь (хуучин зан үйл, backward-compat)
 *
 * Хэрэглэх:
 *   <PreworkBanner mode="worker" />   // зөвхөн ажилтанд харагдана
 *   <PreworkBanner mode="ashua" />    // зөвхөн мастер/хэсгийн даргад
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserContext } from "src/contexts/userContext";
import { getPreworkStatus } from "src/api/hazard";
// ⚠ ӨЭА бүртгэлийг ШУУД биш, кэштэй hook-оор татна — WorkerHero мөн адил
// хэрэглэдэг тул хоёулаа НЭГ хүсэлт хуваалцана (өмнө нь /me хуудсанд
// ижил өгөгдөл хоёр удаа татагддаг байв).
import { fetchMyOea } from "src/hooks/useMyOeaToday";
// ⚠ АШУА-гийн төлөвийг localStorage-аас уншихаа БОЛИВ.
//   Өмнө нь `isAshuaToday` / `listAshuaWorkersToday` / `findAshuaPrework15`
//   гурав нь браузерын санг уншдаг байсан. ӨЭА бүртгэл одоо ЗӨВХӨН баазад
//   бичигддэг тул тэр сан хоосон — банер чимээгүй ажиллахаа болих байлаа.
//   Мөн локал уншилт нь өөр төхөөрөмжөөс бүртгэсэн ажлыг ХЭЗЭЭ Ч
//   харуулдаггүй байв.
import {
  listOeaRegistrations,
  listPreworkSubmissions,
} from "src/api/hazard";

const RED = "#E2382E";

/**
 * ТН тулгалт — тэргүүн тэг зөрдөг (017824 ↔ 17824).
 *
 * ⚠ Түүхийгээр тэнцүүлбэл АШУА өөрийн бүртгэсэн ажилтныг олохгүй,
 *   банер огт гарахгүй. Backend тал ч ижил дүрмээр ажилладаг
 *   (`Models/inspection/assignment_rules.py` · `same_tn`).
 */
const sameTn = (a, b) => {
  const d = (v) => String(v || "").replace(/\D/g, "").replace(/^0+/, "");
  return !!d(a) && d(a) === d(b);
};

// АШУА/мастер/хэсгийн даргын эрхийг тодорхойлох permission-ууд
const ASHUA_PERMS = ["II_III_high_risk_add", "II_III_high_risk_edit"];

export default function PreworkBanner({ style, mode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, checkRole } = useUserContext();
  const [oea, setOea] = useState(null);
  // Урьдчилсан шалгалтын БОДИТ төлөв — xab.prework_checklist_submission-оос.
  // Өмнө нь зөвхөн oea_worker_registrations.checklist_*_completed-ийг
  // хардаг байсан тул бөглөлт шинэ хүснэгтэд орсон ч тольдолт амжаагүй
  // тохиолдолд banner улаан хэвээр үлдэж, "дахин бөглө" гэж шаарддаг байв.
  const [preworkStatus, setPreworkStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const myTn = String(user?.info?.tn || user?.tn || "");

  // ── АШУА-гийн өнөөдрийн төлөв — БААЗААС ──────────────────────────
  // `ashuaWorkers` — өнөөдөр МИНИЙ бүртгэсэн ажилтнууд
  // `ashuaPrev`    — миний өөрийн АШУА 15А бөглөлт (байвал)
  const [ashuaWorkers, setAshuaWorkers] = useState([]);
  const [ashuaPrev, setAshuaPrev] = useState(null);

  // ⚠ Бүртгэл нь асинхроноор ирдэг тул эхний зурагт `false`. Урьд нь
  //   localStorage-аас sync уншдаг тул шууд мэдэгддэг байв. Банер нь
  //   мэдээлэл ирэхэд гарч ирнэ — хоосон банер анивчихаас дээр.
  const isAshuaToday = ashuaWorkers.length > 0;

  // АШУА mode: өнөөдөр ажилтан бүртгэсэн ЭСВЭЛ эрхээрээ мастер
  const hasAshuaPerm = !!(checkRole && checkRole(ASHUA_PERMS));
  const ashuaMode = isAshuaToday || hasAshuaPerm;

  useEffect(() => {
    if (!myTn || !(!mode || mode === "ashua")) return;
    let alive = true;
    const day = new Date().toISOString().slice(0, 10);
    (async () => {
      try {
        // Өнөөдөр МИНИЙ бүртгэсэн ажилтнууд
        const rows = await listOeaRegistrations({ work_date: day });
        const mine = (Array.isArray(rows) ? rows : []).filter(
          (r) => sameTn(r.insert_user_tn, myTn) && r.work_date === day,
        );
        if (alive) setAshuaWorkers(mine);

        // Миний өөрийн АШУА хяналт бөглөгдсөн эсэх
        if (mine.length) {
          const subs = await listPreworkSubmissions({
            tn: myTn,
            work_date: day,
            checker_role: "ASHUA",
            checklist_type: "CRITICAL",
          });
          const list = subs?.items || subs?.data || subs || [];
          if (alive) setAshuaPrev(Array.isArray(list) && list.length ? list[0] : null);
        } else if (alive) {
          setAshuaPrev(null);
        }
      } catch (err) {
        // ⚠ Банер бол НЭМЭЛТ — татагдахгүй бол хуудас ажиллаж байх ёстой.
        //   Гэхдээ чимээгүй болгохгүй: лог үлдээнэ.
        console.warn("АШУА банерын төлөв уншигдсангүй:", err?.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [myTn, mode]);

  // Горимоос хамаарч аль section-ыг үзүүлэхийг тогтооно.
  // `mode`-гүй (хуучин зан үйл) бол хоёулыг хамтад нь үзүүлнэ.
  const showWorker = !mode || mode === "worker";
  const showAshua = !mode || mode === "ashua";

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!myTn || !showWorker || isAshuaToday) {
        // Ажилтны section-г үзүүлэхгүй бол OEA хайх шаардлагагүй.
        // АШУА (localStorage) бол хэрэглэгчийг OEA-аар бас шалгахгүй.
        setLoading(false);
        return;
      }
      try {
        // Бүртгэл (харуулах мэдээлэл) + бөглөлтийн төлөв — зэрэг
        const [r, st] = await Promise.all([
          fetchMyOea(myTn),
          getPreworkStatus({ tn: myTn }).catch(() => null),
        ]);
        if (mounted) {
          setOea(r || null);
          setPreworkStatus(st || null);
        }
      } catch {
        if (mounted) setOea(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [myTn, isAshuaToday, showWorker]);

  if (loading) return null;

  // ── АШУА горим (мастер/хэсгийн дарга) ─────────────────────────────────
  if (showAshua && ashuaMode) {
    if (ashuaPrev) {
      // Бөглөж дуусгасан — ногоон жижиг тэмдэг (улаантай ижил өндөртэй)
      return (
        <div
          style={{
            background: "#EAF6EE",
            border: "1px solid #BEE3C8",
            borderRadius: 7,
            padding: "4px 10px",
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: "#1B7F3B",
            fontSize: 11,
            fontWeight: 600,
            ...style,
          }}
        >
          <i className="fas fa-check-circle" style={{ fontSize: 12 }} />
          <span>
            ✓ АШУА — өнөөдрийн 15 асуумжийн хяналт бөглөгдсөн (
            {ashuaWorkers.length} ажилтан)
          </span>
        </div>
      );
    }
    // АШУА бөглөөгүй — улаан CTA. Энэ нь /highrisk/* БҮХ хуудасны толгойд
    // байнга харагддаг тул нимгэн байх ёстой: өмнө нь 44px icon + 2 мөр
    // тайлбартай, ~80px өндөр байсан нь хуудасны үндсэн агуулгыг доош
    // түлхэж, KPI хэсэг дэлгэцээс гардаг байв.
    return (
      <div
        style={{
          background: "linear-gradient(135deg, #FEEFED 0%, #FFD9D5 100%)",
          border: "1px solid #F2C8C5",
          borderLeft: `4px solid ${RED}`,
          borderRadius: 7,
          padding: "5px 10px",
          marginBottom: 8,
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          ...style,
        }}
      >
        <i
          className="fas fa-user-shield"
          style={{ color: RED, fontSize: 13, flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 200, lineHeight: 1.35 }}>
          <span
            style={{ fontSize: 11.5, fontWeight: 800, color: "#B5251D" }}
          >
            АШУА — өнөөдрийн 15 асуумжийн хяналт хүлээгдэж байна
          </span>
          <span style={{ fontSize: 10.5, color: "#7A2F2A", marginLeft: 8 }}>
            {ashuaWorkers.length} ажилтныг ӨЭА нарядаар бүртгэсэн
          </span>
        </div>
        <button
          type="button"
          onClick={() => navigate("/highrisk/prework15")}
          style={{
            background: RED,
            color: "#fff",
            border: "none",
            borderRadius: 7,
            padding: "3px 10px",
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <i className="fas fa-clipboard-check" style={{ marginRight: 5 }} />
          15 асуумж бөглөх →
        </button>
      </div>
    );
  }

  // ── Ажилтан горим ─────────────────────────────────────────────────────
  if (!showWorker) return null;
  if (!oea) return null;

  // Naryad даалгаврын ажилтан → 5А хуудас руу шилжих banner
  const isNaryad = oea.work_type === "NARYAD";
  const targetPath = isNaryad ? "/highrisk/prework5a" : "/highrisk/prework15";
  // Бөглөсөн эсэх — ЭХЛЭЭД шинэ бөглөлтийн хүснэгтээс (my_submission).
  // Тэр эх сурвалж боломжгүй үед л хуучин denormal тугийг хардана.
  const isCompleted = preworkStatus
    ? !!preworkStatus.my_submission
    : isNaryad
      ? !!oea.checklist_5a_completed
      : !!oea.checklist_15_completed;
  const checklistLabel = isNaryad ? "5А асуумж" : "15 асуумж";

  if (isCompleted) {
    return (
      <div
        style={{
          background: "#EAF6EE",
          border: "1px solid #BEE3C8",
          borderRadius: 10,
          padding: "10px 14px",
          marginBottom: 12,
          display: "flex",
          alignItems: "center",
          gap: 10,
          color: "#1B7F3B",
          fontSize: 13,
          fontWeight: 600,
          flexWrap: "wrap",
          ...style,
        }}
      >
        <i className="fas fa-check-circle" style={{ fontSize: 18 }} />
        <span style={{ flex: 1, minWidth: 220 }}>
          ✓ {checklistLabel} бөглөгдсөн — <b>{oea.department_name || "ажил"}</b>
          {/* Илрүүлсэн аюул байвал — ажил зогссоныг сануулна */}
          {preworkStatus?.my_submission?.hazard_count > 0 ? (
            <span
              style={{ color: "#A3251C", fontWeight: 700, marginLeft: 8 }}
            >
              · {preworkStatus.my_submission.hazard_count} аюул бүртгэсэн —
              удирдлагын шийдвэр хүлээгдэж байна
            </span>
          ) : null}
          {/* ӨЭА ажилд АШУА давхар 15А бөглөх ёстой — хийгээгүй бол сануулна */}
          {preworkStatus?.ashua_required && !preworkStatus?.ashua_done ? (
            <span
              style={{ color: "#8A6D1F", fontWeight: 600, marginLeft: 8 }}
            >
              · АШУА-гийн давхар хяналт хүлээгдэж байна
            </span>
          ) : null}
        </span>
        {/* Аюул мэдээлэх товч — бөглөж дууссан ч ажлын явцад аюул илрэх боломжтой */}
        <button
          type="button"
          onClick={() => navigate("/hazard/register", {
            // Буцахдаа энэ хуудас руугаа эргэж ирнэ
            state: { from: location.pathname },
          })}
          style={{
            background: "#fff",
            color: RED,
            border: `1.5px solid ${RED}`,
            borderRadius: 8,
            padding: "6px 12px",
            fontSize: 12.5,
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <i
            className="fas fa-exclamation-triangle"
            style={{ marginRight: 6 }}
          />
          Аюул мэдээлэх
        </button>
      </div>
    );
  }

  // Бүртгэлтэй ч асуумж бөглөөгүй — улаан CTA banner
  return (
    <div
      style={{
        background: "linear-gradient(135deg, #FEEFED 0%, #FFD9D5 100%)",
        border: "1px solid #F2C8C5",
        borderLeft: `5px solid ${RED}`,
        borderRadius: 12,
        padding: "14px 18px",
        marginBottom: 14,
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
        boxShadow: "0 2px 8px rgba(226,56,46,0.12)",
        ...style,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: RED,
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          flexShrink: 0,
        }}
      >
        <i
          className={
            isNaryad ? "fas fa-clipboard-list" : "fas fa-exclamation-triangle"
          }
        />
      </div>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: "#B5251D",
            marginBottom: 2,
          }}
        >
          {isNaryad
            ? "Та өнөөдөр ажлын даалгаврын ажилд бүртгэгдсэн"
            : "Та өнөөдөр ӨЭА-н ажилд бүртгэгдсэн"}
        </div>
        <div style={{ fontSize: 12.5, color: "#7A2F2A", lineHeight: 1.45 }}>
          Ажилд орохоосоо өмнө <b>{checklistLabel}</b>{" "}
          {isNaryad ? "(5А дадал)" : "(Чухал хяналтын 15 асуумж)"} бөглөж
          зөвшөөрлөө авна уу.
        </div>
        <div
          style={{
            fontSize: 11,
            color: "#7A2F2A",
            marginTop: 4,
            opacity: 0.85,
          }}
        >
          🏢 {oea.department_name || "—"}
          {oea.section_name &&
            oea.section_name !== oea.department_name &&
            ` · 📍 ${oea.section_name}`}
          {oea.destination && ` · ${oea.destination}`}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={() => navigate(targetPath)}
          style={{
            background: RED,
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "10px 18px",
            fontSize: 13.5,
            fontWeight: 800,
            cursor: "pointer",
            whiteSpace: "nowrap",
            boxShadow: "0 2px 6px rgba(226,56,46,0.35)",
          }}
        >
          <i className="fas fa-clipboard-check" style={{ marginRight: 6 }} />
          {checklistLabel} бөглөх →
        </button>
        {/* Аюул мэдээлэх — асуумжтай зэрэгцэж тавьсан хоёр дахь товч */}
        <button
          type="button"
          onClick={() => navigate("/hazard/register", {
            // Буцахдаа энэ хуудас руугаа эргэж ирнэ
            state: { from: location.pathname },
          })}
          style={{
            background: "#fff",
            color: RED,
            border: `2px solid ${RED}`,
            borderRadius: 10,
            padding: "10px 16px",
            fontSize: 13,
            fontWeight: 800,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <i
            className="fas fa-exclamation-triangle"
            style={{ marginRight: 6 }}
          />
          Аюул мэдээлэх
        </button>
      </div>
    </div>
  );
}
